import React, { useEffect, useContext, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";

import videosImg from "../../../assets/course_content_tab/videos.png";
import materialsImg from "../../../assets/course_content_tab/materials.jpg";

import { DarkModeContext } from "../../../Context/DarkModeContext.jsx";
import CourseLinksModal from "../../../Components/CourseLinksModal";

function CourseDetailPage() {
  const { isDarkMode } = useContext(DarkModeContext);
  const location = useLocation();
  const navigate = useNavigate();
  const { courseId } = useParams();
  const { state } = location;
  const courseName = state?.name;
  const [showCourseLinks, setShowCourseLinks] = useState(false);

  // Pagination state
  const cardsPerPage = 4;
  const params = new URLSearchParams(location.search);
  const page = parseInt(params.get("page")) || 1;
  const pageSize = parseInt(params.get("pageSize")) || 20;
  const searchQuery = params.get("searchQuery") || "";
  const sortColumn = params.get("sortColumn") || "Name";
  const sortDirection = params.get("sortDirection") || "asc";

  // Cards data
  const cards = [
    { title: "Videos", image: videosImg, route: "videos" },
    { title: "Materials", image: materialsImg, route: "materials" },
  ];

  // Calculate current cards to display based on pagination
  const indexOfLastCard = cardsPerPage;
  const indexOfFirstCard = indexOfLastCard - cardsPerPage;
  const currentCards = cards.slice(indexOfFirstCard, indexOfLastCard);

  // Effect to scroll to top on page change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [page]);

  // Handlers for pagination
  const goToNextPage = () => {
    navigate(+1);
  };

  const goToPreviousPage = () => {
    const queryParams = new URLSearchParams({
      page,
      pageSize,
      searchQuery,
      sortColumn,
      sortDirection,
    }).toString();

    navigate(`/courses?${queryParams}`);
  };

  // Handler for card click
  const handleCardClick = (route) => {
    if (courseName) {
      const formattedCourseName = courseName.replace(/_/g, " ");
      navigate(`/courses/${formattedCourseName}/${route}`, {
        state: { courseName, courseId },
      });
    }
  };

  // Format path to display course name instead of courseId
  const pathSegments = location.pathname.slice(1).split("/");

  const formattedPath = pathSegments
    .map((segment) => {
      // If it's the courseId part, replace it with the courseName
      if (segment === courseId && courseName) {
        return courseName;
      }
      return segment;
    })
    .join(" > ");

  return (
    <div
      className={`flex flex-col lg:flex-row lg:items-start ${
        isDarkMode ? "bg-[#292929] text-black" : "bg-white text-black"
      } p-4 lg:p-8 min-h-screen`}
    >
      <CourseLinksModal open={showCourseLinks} onClose={() => setShowCourseLinks(false)} courseId={courseId} />
      <div
        className={`${
          isDarkMode ? "bg-[#1e1e1e] text-white" : "bg-white text-black"
        } p-4 lg:p-8 mt-[40px] lg:mt-0 rounded-lg shadow-xl flex-1`}
      >
        {/* PATH AND CONTROLS */}
        <div className="lg:flex w-full items-center mb-4 text-gray-700 text-sm capitalize">
          <div className="flex w-[58px] gap-1">
            <button
              onClick={goToPreviousPage}
              className="w-[24px] h-[20px] flex justify-center items-center bg-gray-300 rounded-[6px] disabled:opacity-50"
            >
              <IoIosArrowBack />
            </button>
            <button
              onClick={goToNextPage}
              className="w-[24px] h-[20px] flex justify-center items-center bg-gray-300 rounded-[6px] disabled:opacity-50"
            >
              <IoIosArrowForward />
            </button>
          </div>

          <div
            className={`${
              isDarkMode ? "text-white" : "text-black"
            } flex items-center text-sm capitalize p-2`}
          >
            <span>{formattedPath}</span>
          </div>
        </div>

        <div className="mb-5 flex justify-end">
          <button type="button" onClick={() => setShowCourseLinks(true)} className="rounded-xl bg-blue-600 px-5 py-3 text-sm font-semibold text-white hover:bg-blue-700">Course links</button>
        </div>
        {/* Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {currentCards.map((card, index) => (
            <div
              key={index}
              className="relative bg-white rounded-[10px] shadow-md overflow-hidden flex justify-center items-center cursor-pointer hover:scale-customScale ease-in-out duration-300"
              onClick={() => handleCardClick(card.route)}
            >
              <img
                src={card.image}
                alt={card.title}
                className={`w-full h-64 object-cover ${
                  isDarkMode ? "opacity-100" : "opacity-80"
                }`}
              />
              <div className="absolute inset-0 flex justify-center items-center">
                <h3 className="text-white lg:text-[50px] md:text-[30px] text-xl font-bold">
                  {card.title}
                </h3>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default CourseDetailPage;
